import { api, ApiError } from '../api/client.js';
/**
 * The upload queue — TAD 3.2.2.
 *
 * WHAT THIS IS, PRECISELY
 * Durable local custody of captured photos. It holds them until the user sends
 * them, then until the network accepts them, and retries.
 *
 * IT HOLDS TWO KINDS OF ROW, and the difference is the `staged` flag.
 *
 * A STAGED row is a draft. FR-04's capture flow lets a salesperson take up to
 * seven photos, look at them, retake the blurred one and delete the accidental
 * one, and only then press Analisis. Until that press the photo exists nowhere
 * else, so the queue is what stops a killed app from costing seven photographs.
 * `drain()` skips these — the background auto-drain must never send something
 * the user is still deciding about.
 *
 * A COMMITTED row is the original meaning: the user has sent it and the network
 * has not accepted it yet.
 *
 * WHAT IT IS NOT
 * Offline operation. SPK Pasal 2(4) puts offline-first and synchronisation
 * queueing out of scope, and TAD 3.2.2 asks the client to acknowledge the
 * distinction in writing. So there is no offline check-in, no local visit
 * creation, no conflict resolution — every queued photo, draft or not, belongs
 * to a visit the server already knows about, and the queue cannot start new work.
 *
 * It exists because field sales in Indonesia routinely lose signal inside
 * warehouses and basement retail, and a salesperson who has taken seven photos
 * should not lose them to a dead zone.
 *
 * The logic below is platform-free and the storage is not, so the storage sits
 * behind `UploadQueueDriver`. Only the driver changed when this moved out of the
 * PWA; the sequence, the retry accounting and the idempotency did not.
 */
let driver = null;
/** Call once from the app entrypoint, before anything can capture a photo. */
export function configureUploadQueue(next) {
    driver = next;
}
function required() {
    if (driver === null) {
        throw new Error('configureUploadQueue() must be called before the upload queue is used.');
    }
    return driver;
}
/**
 * Runs one store mutation at a time, in the order the callers asked for.
 *
 * WHY THE DRIVERS NEED THIS. Neither of them is transactional, and several of
 * the operations below are read-modify-write: `discardDraft` lists, finds and
 * then removes; the browser driver's `update` reads a row, awaits, and writes it
 * back. Two of those interleaving is how a row gets resurrected after it was
 * deleted — a record whose bytes are gone, retrying forever against a file that
 * will never exist. `drain`'s `inFlight` guard has always said so in its own
 * comment; it just only ever covered the drain against ITSELF, and the drain is
 * not the only writer. `discardAllFor` runs from a check-out, `commitStaged`
 * from an Analisis, `sweepOrphans` from startup, and the auto-drain runs from a
 * connectivity event that no screen can see coming.
 *
 * IT WRAPS THE STORE CALLS AND NOT THE DRAIN LOOP, which is the whole design.
 * Holding this across `run()` would put the network inside the critical section:
 * a capture would then block behind however long a background drain takes, on
 * shop signal, with the shutter button reading "Memproses…" the entire time. The
 * sections below are local storage only — microseconds — so nothing that the
 * user is waiting on ever queues behind anything slow. Interleaving BETWEEN two
 * photos of a drain is harmless and stays allowed: the drain re-checks nothing
 * it has not already snapshotted, and both drivers make an `update` of a row
 * that has since been removed a no-op.
 *
 * A rejection must not poison the chain — the tail swallows it, while the
 * caller still gets its own rejection.
 */
let tail = Promise.resolve();
function serial(operation) {
    const next = tail.then(operation, operation);
    tail = next.then(() => undefined, () => undefined);
    return next;
}
export async function enqueue(photo, body) {
    const store = required();
    await serial(() => store.put({ ...photo, attempts: 0, lastError: null }, body));
}
export async function pending() {
    const store = required();
    // Read through the lock. `discardAllFor`, `clearQueue` and `commitStaged` walk
    // their rows one at a time, so a read taken outside it can land between two
    // iterations and answer with a set that never existed as a whole.
    return serial(() => store.list());
}
export async function remove(localId) {
    const store = required();
    await serial(() => store.remove(localId));
}
/**
 * Holds a freshly captured photo as a draft — FR-04's staging flow.
 *
 * DELIBERATELY DOES NOT ENFORCE THE SEVEN-PHOTO CAP. A retake stages the
 * replacement BEFORE discarding the original (so a crash between the two leaves
 * a visible duplicate rather than nothing at all), which is transiently eight.
 * The cap belongs on the capture button, where it can be shown to the user
 * before they take a photo rather than refused after.
 */
export async function stage(photo, body) {
    const store = required();
    await serial(() => store.put({ ...photo, staged: true, attempts: 0, lastError: null }, body));
}
/**
 * Every local row for one visit, drafts and unsent alike, in grid order.
 *
 * BOTH KINDS, and that is the point rather than laziness. After a partial batch
 * the failures are committed rows still sitting in the queue: not drafts any
 * more, but not on the server either. A screen that listed only drafts would
 * show seven thumbnails collapse into five result cards with the other two
 * nowhere, and the user would reasonably conclude two photos were lost.
 */
export async function pendingFor(visitId) {
    const store = required();
    // Through the lock, for the reason `pending` gives: this is what the draft grid
    // renders from, and half of a multi-row deletion is not a state to draw.
    const rows = await serial(() => store.list());
    return rows.filter((photo) => photo.visitId === visitId).sort((a, b) => a.slot - b.slot);
}
/**
 * Discards a draft — the user deleted it, or replaced it with a retake.
 *
 * Guarded to staged rows. A committed row is either in flight or waiting to
 * retry, and deleting one from a mis-wired button would silently drop a photo
 * the user believes they have already sent.
 */
export async function discardDraft(localId) {
    const store = required();
    // List, decide and remove as one section: the decision is about a row another
    // writer may be removing, and a `remove` between the two is a check-then-act.
    await serial(async () => {
        const photo = (await store.list()).find((row) => row.localId === localId);
        if (!photo || !photo.staged)
            return;
        await store.remove(localId);
    });
}
/**
 * A renderable URI for a queued photo's bytes, plus the way to let it go.
 *
 * Callers MUST call `release` — see `UploadQueueDriver.preview` for why. In
 * practice `useVisitDrafts` owns this and nothing else should need it.
 */
export async function previewOf(localId) {
    return required().preview(localId);
}
/** Drops everything still held for a visit. Used when the visit closes. */
export async function discardAllFor(visitId) {
    const store = required();
    await serial(async () => {
        const rows = (await store.list()).filter((photo) => photo.visitId === visitId);
        for (const photo of rows)
            await store.remove(photo.localId);
    });
}
/**
 * Throws the whole queue away, bytes included. The user asked.
 *
 * THE ESCAPE HATCH FOR A QUEUE THAT CANNOT DRAIN. Every other removal path is
 * addressed at something — a draft the user deleted, a visit that closed — and
 * none of them helps a row that fails forever: a photo whose bytes the driver
 * lost, or one wedged behind an error `run()` treats as retryable when it is
 * not. The count then sits on the profile screen for the rest of the install
 * saying photos are waiting to be sent, and nothing the user can reach makes it
 * true or false.
 *
 * DESTRUCTIVE AND NOT UNDOABLE, so the CALLER must confirm it. This function
 * deliberately takes no "are you sure" flag: a guard here would be a second
 * opinion about a decision the screen has already put in front of the person who
 * owns the photographs.
 *
 * Returns how many rows went, so the screen can say what it did rather than
 * leaving the user to infer it from a counter that reached zero.
 */
export async function clearQueue() {
    const store = required();
    /*
     * STOP SENDING, THEN DELETE — and in that order, because the reverse lies.
     *
     * The drain's network calls are deliberately outside the store lock, so a
     * `clearQueue` racing one deletes rows the drain has ALREADY delivered and
     * then reports them as thrown away: "5 foto dihapus dari antrean" over
     * photographs that reached the server. The native driver makes the mirror
     * image possible too — `remove` unlinks the file, but an upload already
     * holding the descriptor finishes anyway, so a photo the user asked to destroy
     * still lands in storage.
     *
     * `abortDrain` bounds the wait to the photo currently in flight rather than to
     * the whole batch, which matters: this is the escape hatch for a queue that
     * will not drain, and it must not itself hang on the thing it is escaping.
     */
    abortDrain();
    await settled(inFlight);
    return serial(async () => {
        const rows = await store.list();
        for (const photo of rows)
            await store.remove(photo.localId);
        return rows.length;
    });
}
/**
 * Drops drafts that belong to no open visit. Call once the server has said which
 * visit is open — `null` when none is.
 *
 * WHY A DRAFT CAN OUTLIVE ITS VISIT AT ALL. `drain()` never sends a staged row,
 * because a draft is a photograph the user is still deciding about. That is
 * right while the visit is open and a trap the moment it is not: the row is
 * skipped by the drain, unreachable from every screen but the one visit it
 * belongs to, and that screen redirects away the moment it learns the visit has
 * closed. It then sits on the profile tab for the life of the install saying a
 * photo is waiting to be sent, and nothing the user can reach makes that true or
 * false. `clearQueue()` was the only way out, and it takes everything with it.
 *
 * The races that leave one behind are now closed on the client, but this heals
 * the handsets that already carry one — and it is the honest backstop for the
 * paths no lock can reach: another device closing the visit, or this app being
 * killed while the camera is up.
 *
 * COMMITTED ROWS ARE LEFT ALONE, and that restraint is the whole safety of it.
 * Those are photographs the user has confirmed; they belong to whichever visit
 * they name, they drain on their own, and a 409 from a closed visit is what
 * removes them — with the count reported to the screen. Deleting them here would
 * be this function quietly doing what only a dead letter or an explicit
 * check-out is allowed to do.
 *
 * Returns how many went, so a caller can say so rather than leave the user to
 * infer it from a counter that moved on its own.
 */
export async function discardStaleDrafts(activeVisitId) {
    const store = required();
    return serial(async () => {
        const stale = (await store.list()).filter((photo) => photo.staged && photo.visitId !== activeVisitId);
        for (const photo of stale)
            await store.remove(photo.localId);
        return stale.length;
    });
}
/**
 * Promotes a visit's drafts to committed rows. The user pressed Analisis.
 *
 * A separate step from `drain()` so the promotion is durable before the network
 * is touched: an app killed mid-batch resumes with photos that are still going
 * to be sent, rather than drafts it has forgotten the user confirmed.
 */
export async function commitStaged(visitId) {
    const store = required();
    return serial(async () => {
        const drafts = (await store.list()).filter((photo) => photo.visitId === visitId && photo.staged);
        for (const photo of drafts)
            await store.update({ ...photo, staged: false });
        return drafts.length;
    });
}
export function drain(onProgress) {
    /*
     * ONE PASS AT A TIME — BUT A LATER CALLER GETS A LATER PASS.
     *
     * Two concurrent passes over the same rows must not happen: `startAutoDrain`
     * is live for the whole session, so an `online` event or an AppState flip to
     * active while a batch is uploading would otherwise start a second one.
     *
     * THIS USED TO HAND THE RUNNING PASS'S PROMISE TO EVERY LATER CALLER, AND THAT
     * COST PHOTOGRAPHS. A pass snapshots the queue when it starts. Returning from
     * the OS picker fires AppState → active, so the auto-drain is very often
     * already running over yesterday's failures when the user presses Analisis —
     * and Analisis commits three fresh drafts and then calls this. The answer it
     * got back described a batch that never contained them: "2 foto terkirim" for
     * a press that meant five. The three were left as committed rows with no
     * attempts against them, which is the one state the screen rendered no retry
     * control for, and the next check-out deleted them.
     *
     * So a caller arriving mid-pass now gets a promise for a SUBSEQUENT pass, one
     * that will snapshot after their rows were committed. At most one is queued;
     * everybody who arrives during the current pass joins it. The result they
     * receive is then about their own photographs, which is what they asked.
     */
    if (inFlight === null) {
        inFlight = run(onProgress).finally(() => {
            inFlight = null;
        });
        return inFlight;
    }
    // The newest caller's progress callback wins: it belongs to whoever is
    // watching a button, and the pass they are waiting on is the queued one.
    queuedProgress = onProgress ?? queuedProgress;
    queued ??= settled(inFlight).then(() => {
        const onQueuedProgress = queuedProgress;
        queued = null;
        queuedProgress = undefined;
        inFlight = run(onQueuedProgress).finally(() => {
            inFlight = null;
        });
        return inFlight;
    });
    return queued;
}
/**
 * Asks the running pass to stop after the photo it is on.
 *
 * TWO CALLERS, BOTH OF WHICH ARE ABOUT TO INVALIDATE THE WORK. `clearQueue` is
 * throwing the rows away, and sign-out is ending the session the requests are
 * authenticated as — a loop left running there issues `POST /photos/presign` for
 * the previous salesperson's visit under the NEXT one's bearer token, on a
 * handset the two of them share.
 *
 * A generation counter rather than a boolean, so a drain started after the abort
 * is unaffected by it and nothing has to remember to clear a flag.
 */
export function abortDrain() {
    generation += 1;
}
/** Settles with the promise, never rejecting — so a failed pass cannot break the chain. */
function settled(promise) {
    return promise === null
        ? Promise.resolve()
        : promise.then(() => undefined, () => undefined);
}
let inFlight = null;
let queued = null;
let queuedProgress;
let generation = 0;
async function run(onProgress) {
    const store = required();
    const startedAt = generation;
    /*
     * THE SNAPSHOT IS TAKEN UNDER THE LOCK, unlike the per-photo work below it.
     *
     * `commitStaged` promotes a visit's drafts one row at a time inside its own
     * critical section. A list taken outside the lock can land between two of
     * those writes and snapshot a half-promoted batch — sending some of a press
     * and silently leaving the rest as committed rows nothing will pick up until
     * the next pass.
     *
     * Drafts are excluded here rather than at the call sites, so there is no way
     * to send one by reaching for the wrong function. `staged === undefined` on a
     * row written before staging existed is falsy, so it drains — correct, it was
     * already a committed upload waiting for the network.
     */
    const queue = (await serial(() => store.list())).filter((photo) => !photo.staged);
    let uploaded = 0;
    let failed = 0;
    let dropped = 0;
    for (const photo of queue) {
        /*
         * Checked between photos rather than mid-request: a half-finished upload has
         * to be allowed to resolve so its `remove` or its attempt counter is written
         * honestly. The rows left unvisited stay queued, which is the truth — they
         * were not sent.
         */
        if (startedAt !== generation)
            break;
        try {
            const presigned = await api.post('/photos/presign', {
                visitId: photo.visitId,
                contentType: 'image/jpeg',
                contentLength: photo.bytes,
            });
            await store.upload(presigned.uploadUrl, photo.localId, 'image/jpeg');
            // No `photoType`: the model classifies the page (FR-05).
            await api.post('/photos', {
                visitId: photo.visitId,
                objectKey: presigned.objectKey,
                captureSource: photo.captureSource,
                exif: photo.exif,
                capturedAt: photo.capturedAt,
                compressedBytes: photo.bytes,
                // Absent on a row queued by an older build; the contract makes all five
                // optional so such a row still registers rather than 400ing forever.
                width: photo.width,
                height: photo.height,
                sourceWidth: photo.sourceWidth,
                sourceHeight: photo.sourceHeight,
                sourceBytes: photo.sourceBytes,
                // Measured at the shutter and carried here, not recomputed — see
                // `QueuedPhoto.captureQuality`. Absent on an older row, like the five
                // above, and absent means "nobody looked" rather than "it was fine".
                captureQuality: photo.captureQuality,
                captureSharpness: photo.captureSharpness,
                idempotencyKey: photo.localId,
            });
            await serial(() => store.remove(photo.localId));
            uploaded += 1;
        }
        catch (error) {
            failed += 1;
            /*
             * 404 and 409 are dead letters, not failures to retry.
             *
             * Both mean the server will never accept this photo: the visit is gone, or
             * it is CLOSED and `nextVisitState(CLOSED, 'ADD_PHOTO')` is null. Keeping
             * the row means retrying on every reconnect for the life of the install,
             * against a file that also never gets reclaimed. Dropping it is the honest
             * outcome — and it is counted as failed, so the caller still reports it.
             *
             * COUNTED IN `dropped` AS WELL, because "failed" alone is not what became
             * of it. The screen's message for a failure is that the photo will be sent
             * when the signal returns, and for these it will not: the row has just
             * been deleted. See `DrainResult`.
             */
            if (error instanceof ApiError && (error.status === 404 || error.status === 409)) {
                await serial(() => store.remove(photo.localId));
                dropped += 1;
            }
            else {
                await serial(() => store.update({
                    ...photo,
                    attempts: photo.attempts + 1,
                    lastError: error instanceof Error ? error.message : String(error),
                }));
            }
        }
        onProgress?.(queue.length - uploaded - failed);
    }
    return { uploaded, failed, dropped };
}
/**
 * Drains when the platform says the network is back.
 *
 * Connectivity is a hint rather than a guarantee — a phone on a captive portal
 * reports online — so a failed drain simply leaves the photos queued for the
 * next attempt.
 *
 * `onDrained` EXISTS BECAUSE NOTHING ELSE CAN SEE THIS HAPPEN. Every other write
 * to the queue is made by a screen, which knows to refresh itself afterwards.
 * This one is made by a subscription that outlives every screen, so the profile's
 * "Antrean unggah" count sat at the number it had when the tab was first opened
 * while the photos it was counting were being sent underneath it — the queue
 * emptied and the only thing that reports on it went on saying otherwise.
 *
 * Called after the drain settles, whatever it settled as: a partial drain still
 * changes the count, and so does a failure that bumped every attempt counter.
 */
export function startAutoDrain(onDrained) {
    return required().subscribeToConnectivity(() => {
        void drain().then((result) => onDrained?.(result), 
        // `drain` resolves rather than rejects on an upload that failed — this is
        // the driver or the store itself having gone wrong. The count is still
        // worth re-reading; there is simply no result to describe it with.
        () => onDrained?.({ uploaded: 0, failed: 0, dropped: 0 }));
    });
}
/** Deletes bytes no record points at. Call once at startup, alongside `startAutoDrain`. */
export async function sweepOrphans() {
    const store = required();
    // Serialised against `stage`, and that pairing is the reason it is here at
    // all. A sweep reads the rows and then deletes every file no row names — so
    // one running in the gap between a capture writing its bytes and inserting its
    // record would delete exactly the photograph that was being saved, leaving the
    // record pointing at nothing. That is the one inconsistency the drivers'
    // ordering rules promise is unreachable.
    await serial(() => store.sweepOrphans());
}
//# sourceMappingURL=queue.js.map